import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Alert,
} from 'reactstrap';
import {
  addNewPromotionData,
  getPromotionData,
  newPromotionSaveErrored,
  redirectToOfferListPage,
} from '../../actions/promotionActions';
import { ActivateOfferData } from '../../actions/offerActions';
import { BeatLoader } from 'react-spinners';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import NoRowExistsRow from '../../components/common/noRowExistsRow';
import OfferRow from '../../components/campaign/offerRow';
import { useParams, useNavigate } from 'react-router-dom';

const PromotionEditPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaignId, promotionId } = useParams();

  // Redux state
  const promotion = useSelector((state) => state.promotionDetails);
  const isSuccess = useSelector(
    (state) => state.isNewPromotionSaveSuccess?.isSavedSuccess,
  );
  const savedPromotion = useSelector((state) => state.promotion);
  const hasErrored = useSelector((state) => state.isNewPromotionSaveErrored);
  const errorMessage = useSelector(
    (state) => state.isNewPromotionSaveErrored?.message || '',
  );
  const isSaving = useSelector((state) => state.isNewCampaignIsSaving);
  const redirectToPromotionListPage = useSelector(
    (state) => state.redirectToPromotionListPage,
  );
  const isLoading = useSelector((state) => state.promotionDetailsIsLoading);

  // Local state
  const [externalId, setExternalId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [startDate, setStartDate] = useState(promotionId ? moment() : null);
  const [endDate, setEndDate] = useState(promotionId ? moment() : null);
  const [offers, setOffers] = useState([]);
  const [emptyRowString] = useState('No Row Exist');
  const [isFormValid, setIsFormValid] = useState(false);
  const [pageTitle, setPageTitle] = useState(promotionId ? 'Edit' : 'Add New');
  const [isPromotionIdEnabled, setIsPromotionIdEnabled] =
    useState(!promotionId);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Error flags
  const [errors, setErrors] = useState({
    source: false,
    externalId: false,
    campaignName: false,
    campaignDescription: false,
    startDate: false,
    endDate: false,
    status: false,
  });

  // Fetch promotion data on mount
  useEffect(() => {
    dispatch(newPromotionSaveErrored(false));
    dispatch(redirectToOfferListPage(false));

    if (promotionId) {
      dispatch(getPromotionData(promotionId));
      setIsFormValid(true);
      setPageTitle('Edit');
      setIsPromotionIdEnabled(false);
    }
  }, [dispatch, promotionId]);

  // Update local state when promotion data changes
  useEffect(() => {
    if (promotionId && promotion) {
      setExternalId(promotion.externalId || '');
      setCampaignName(promotion.name || '');
      setCampaignDescription(promotion.description || '');
      setStartDate(promotion.startDate ? moment(promotion.startDate) : null);
      setEndDate(promotion.endDate ? moment(promotion.endDate) : null);
      setOffers(promotion.offers || []);
      setIsReadOnly(!!promotion.isReadOnly);
    }
  }, [promotionId, promotion]);

  // Redirect if needed
  useEffect(() => {
    if (redirectToPromotionListPage) {
      navigate(`/campaign/${campaignId}`);
    }
  }, [redirectToPromotionListPage, navigate, campaignId]);

  // Form validation
  const validateForm = useCallback(
    (sDate = startDate, eDate = endDate) => {
      const newErrors = { ...errors };
      let valid = true;

      const isSDateEmpty = !sDate;
      const isEDateEmpty = !eDate;
      if (isSDateEmpty || isEDateEmpty) {
        newErrors.startDate = true;
        newErrors.endDate = true;
        valid = false;
      } else {
        const sDateStr = sDate.format('DD-MMM-YYYY');
        const eDateStr = eDate.format('DD-MMM-YYYY');
        const isSameOrBefore = moment(eDateStr).isSameOrBefore(sDateStr);
        if (isSameOrBefore) {
          alert('Invalid date');
          newErrors.startDate = true;
          newErrors.endDate = true;
          valid = false;
        } else {
          newErrors.startDate = false;
          newErrors.endDate = false;
        }
      }
      setErrors(newErrors);
      setIsFormValid(valid);
      return valid;
    },
    [startDate, endDate, errors],
  );

  // Handlers
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case 'externalId':
        setExternalId(value);
        break;
      case 'campaignName':
        setCampaignName(value);
        break;
      case 'campaignDescription':
        setCampaignDescription(value);
        break;
      default:
        break;
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    validateForm(date, endDate); // Pass new startDate
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    validateForm(startDate, date); // Pass new endDate
  };

  const canSubmitSearchForm = () => true; // Placeholder

  const handleSubmitAddForm = (e) => {
    e.preventDefault();
    if (!canSubmitSearchForm()) return;
    const newPromotion = {
      source: promotion?.source,
      externalId,
      name: campaignName,
      description: campaignDescription,
      startDate: startDate ? moment(startDate).format('DD-MMM-YYYY') : '',
      endDate: endDate ? moment(endDate).format('DD-MMM-YYYY') : '',
      IsActive: true,
      campaignId,
    };
    if (promotionId) newPromotion.id = promotionId;
    dispatch(addNewPromotionData(newPromotion));
  };

  const handleActivateOffer = async (id) => {
    await dispatch(ActivateOfferData(id));
    setTimeout(() => {
      dispatch(getPromotionData(promotionId));
    }, 5000);
  };

  const getOfferRows = (offers, handleActivateOffer) => {
    return Array.isArray(offers) && offers.length > 0 ? (
      offers.map((offer, index) => (
        <div className='panel panel-default' key={index}>
          <OfferRow offer={offer} activateOffer={handleActivateOffer} />
        </div>
      ))
    ) : !isSaving ? (
      <NoRowExistsRow emptyRowText={emptyRowString} />
    ) : (
      <></>
    );
  };

  // Render
  const errorMessageAlert = (
    <Row>
      <Col md={12}>
        <Alert color='danger'>{errorMessage}</Alert>
      </Col>
    </Row>
  );

  const offerRows = isLoading ? (
    <Row>
      <Col>
        <BeatLoader color={'#004C5F'} loading data-testid='beat-loader' />
      </Col>
    </Row>
  ) : (
    getOfferRows(offers, handleActivateOffer)
  );

  return (
    <div>
      <h4>{pageTitle} Promotion </h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Form onSubmit={handleSubmitAddForm}>
              {hasErrored && errorMessage.length > 0 ? errorMessageAlert : null}
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='externalId' md={1}>
                      Promotion Id
                    </Label>
                    <Col md={2} sm={10}>
                      <Input
                        disabled={!isPromotionIdEnabled || isReadOnly}
                        type='text'
                        name='externalId'
                        id='externalId'
                        value={externalId}
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                        maxLength='20'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='campaignName' md={1}>
                      Promotion Name
                    </Label>
                    <Col md={5} sm={10}>
                      <Input
                        disabled={isReadOnly}
                        type='text'
                        name='campaignName'
                        id='campaignName'
                        value={campaignName}
                        required
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='campaignDescription' md={1}>
                      Description
                    </Label>
                    <Col md={10} sm={11}>
                      <Input
                        disabled={isReadOnly}
                        type='text'
                        name='campaignDescription'
                        id='campaignDescription'
                        value={campaignDescription}
                        required
                        onChange={handleInputChange}
                        className={errors.source ? 'error' : ''}
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='startDate' md={1}>
                      Start Date
                    </Label>
                    <Col md={10} sm={10}>
                      <DatePicker
                        disabled={isReadOnly}
                        id='startDate'
                        name='startDate'
                        required
                        selected={startDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleStartDateChange}
                        className={errors.startDate ? 'error' : 'form-control'}
                        locale='en-au'
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FormGroup row>
                    <Label for='endDate' md={1}>
                      End Date
                    </Label>
                    <Col md={10} sm={10}>
                      <DatePicker
                        disabled={isReadOnly}
                        id='endDate'
                        name='endDate'
                        required
                        selected={endDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={handleEndDateChange}
                        className={errors.endDate ? 'error' : 'form-control'}
                      />
                    </Col>
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className='me-sm-2 mb-sm-0'>
                <Button
                  color='primary'
                  name='btnFilter'
                  id='btnFilter'
                  disabled={!isFormValid || isReadOnly}
                >
                  Save
                </Button>
                <BeatLoader color={'#004C5F'} loading={isSaving} />
              </FormGroup>
            </Form>
          </Container>
        </CardBody>
      </Card>
      <h4>Offers</h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Row>
              <Col md='1' className='column-header white-border'>
                Offer Id
              </Col>
              <Col md='5' className='column-header white-border'>
                Name
              </Col>
              <Col md='1' className='column-header white-border'>
                Start Date
              </Col>
              <Col md='1' className='column-header white-border'>
                End Date
              </Col>
              <Col md='2' className='column-header white-border'>
                Status
              </Col>
              <Col md='1' className='column-header white-border'></Col>
              <Col md='1' className='column-header white-border'></Col>
            </Row>
            {offerRows}
          </Container>
        </CardBody>
      </Card>
    </div>
  );
};

export default PromotionEditPage;
